import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { InitialDataService } from '@services/loadInitialData';
import { collectionRef, Report, TextWidget } from '@vincerosoft/assessfluence-types';
import { where } from 'firebase/firestore';

type ReportResourceType = 'form' | 'form-group' | 'solver';

type LegacyReportFields = {
  source_type?: ReportResourceType;
  form_id?: string;
  form_group_id?: string;
  solver_id?: string;
};

const reportWithLegacyFields = (report: Report) => report as Report & LegacyReportFields;

/** Prefers `resource.type`, falls back to legacy `source_type`, then to whichever legacy id exists. */
export const getReportResourceType = (report: Report): ReportResourceType | undefined => {
  const r = reportWithLegacyFields(report);
  if (r.resource?.type) return r.resource.type;
  if (r.source_type) return r.source_type;
  if (r.form_group_id) return 'form-group';
  if (r.solver_id) return 'solver';
  if (r.form_id) return 'form';
  return undefined;
};

/** Prefers `resource.id`, falls back to the legacy id matching the resource type. */
export const getReportResourceId = (report: Report): string | undefined => {
  const r = reportWithLegacyFields(report);
  if (r.resource?.id) return r.resource.id;
  switch (getReportResourceType(report)) {
    case 'form-group':
      return r.form_group_id;
    case 'solver':
      return r.solver_id;
    case 'form':
      return r.form_id;
    default:
      return undefined;
  }
};

function reportsFactory() {
  async function loadReportsStore(
    onProgress: (data: { reports: Report[]; textwidgets: TextWidget[] }) => void
  ) {
    const inFlightStore: {
      reports: Report[];
      textwidgets: TextWidget[];
    } = {
      reports: [],
      textwidgets: []
    };

    await InitialDataService.load<Report>(collectionRef.reports, [], {}, (reports) => {
      inFlightStore.reports = reports;
      onProgress({ ...inFlightStore });
    });

    await InitialDataService.load<TextWidget>(collectionRef.textwidgets, [], {}, (textWidgets) => {
      inFlightStore.textwidgets = textWidgets;
      onProgress({ ...inFlightStore });
    });

    return inFlightStore;
  }

  async function getReportById(reportId: string) {
    return collectionsService.reports.getDoc(reportId);
  }

  async function getReportsByIds(reportIds: string[], onProgress?: (data: Report[]) => void) {
    const reports = await collectionsService.reports.getDocsInBatches(
      ['id', 'in', reportIds],
      undefined,
      onProgress
    );
    return reports.flat();
  }

  async function getReportsByFormGroupId(formId: string, scope: Report['scope']) {
    return collectionsService.reports.getDocs([
      where('resource.id', '==', formId),
      where('scope', '==', scope)
    ]);
  }

  async function getReportsByFormId(formId: string, scope: Report['scope']) {
    return collectionsService.reports.getDocs([
      where('resource.id', '==', formId),
      where('scope', '==', scope)
    ]);
  }

  async function getReportsByFormIds(
    formIds: string[],
    scope: Report['scope'] | 'insights',
    onProgress?: (data: Report[]) => void
  ) {
    const reports = await collectionsService.reports.getDocsInBatches(
      ['resource.id', 'in', formIds],
      ...(scope !== 'insights' ? [where('scope', '==', scope)] : []),
      onProgress
    );
    return reports.flat();
  }

  async function getReportsByFormGroupIds(
    formGroupIds: string[],
    scope: Report['scope'] | 'insights',
    onProgress?: (data: Report[]) => void
  ) {
    const reports = await collectionsService.reports.getDocsInBatches(
      ['resource.id', 'in', formGroupIds],
      ...(scope !== 'insights' ? [where('scope', '==', scope)] : []),
      onProgress
    );
    return reports.flat();
  }

  async function getReportsForSolver(solverId: string) {
    return collectionsService.reports.getDocs([where('resource.id', '==', solverId)]);
  }

  return {
    getReportById,
    getReportsByIds,
    getReportsByFormIds,
    getReportsByFormGroupIds,
    getReportsByFormId,
    getReportsForSolver,
    getReportsByFormGroupId,
    loadReportsStore
  };
}

export const reportsService = reportsFactory();
